import type { ReactNode } from "react";
import { Video, X } from "lucide-react";
import { AvatarBubble, ChatRoundCheck, ClientPill } from "@/components/chatChrome";
import {
  colleagueSharesClients,
  intersectColleagueClients,
} from "@/lib/chatFormat";
import { isChatPeerBusyInCall, normalizeChatPresence, type ChatColleague, type ChatConversation } from "@/lib/chatApi";

type ColleagueRowProps = {
  colleague: ChatColleague;
  presence: Record<number, string>;
  trailing?: ReactNode;
  disabled?: boolean;
  hint?: string;
  onPick: () => void;
  onOpenAvatarPreview: (url: string | null | undefined, alt?: string) => void;
};

function ColleagueRow({
  colleague,
  presence,
  trailing,
  disabled,
  hint,
  onPick,
  onOpenAvatarPreview,
}: ColleagueRowProps) {
  const name = colleague.display_name?.trim() || colleague.username;
  return (
    <button
      type="button"
      disabled={disabled}
      className={`flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left ${
        disabled
          ? "cursor-not-allowed opacity-50"
          : "hover:bg-[var(--chat-surface-elevated)]"
      }`}
      onClick={onPick}
    >
      <AvatarBubble
        label={name}
        url={colleague.avatar_url}
        presence={normalizeChatPresence(presence[colleague.id])}
        size="sm"
        onClick={
          colleague.avatar_url
            ? () => onOpenAvatarPreview(colleague.avatar_url, name)
            : undefined
        }
      />
      <span className="min-w-0 flex-1">
        <span className="flex min-w-0 items-center gap-1.5">
          <span className="truncate text-sm font-medium">{name}</span>
          {(colleague.clients ?? []).slice(0, 2).map((cl) => (
            <ClientPill key={cl.id} name={cl.name} />
          ))}
        </span>
        {hint ? (
          <span className="block truncate text-xs text-[var(--chat-muted)]">{hint}</span>
        ) : colleague.email ? (
          <span className="block truncate text-xs text-[var(--chat-muted)]">{colleague.email}</span>
        ) : null}
      </span>
      {trailing}
    </button>
  );
}

type Shared = {
  selfId: number;
  colleagues: ChatColleague[];
  presence: Record<number, string>;
  onOpenAvatarPreview: (url: string | null | undefined, alt?: string) => void;
};

export function ChatNewDmModal({
  selfId,
  colleagues,
  presence,
  onClose,
  onStartDm,
  onOpenAvatarPreview,
}: Shared & {
  onClose: () => void;
  onStartDm: (userId: number) => void;
}) {
  const others = colleagues.filter((c) => c.id !== selfId);
  return (
    <div className="fixed inset-0 z-40 flex items-end justify-center bg-black/40 p-4 sm:items-center">
      <div className="chat-panel max-h-[80vh] w-full max-w-md overflow-hidden rounded-2xl border shadow-[var(--chat-shadow)]">
        <header className="flex items-center justify-between border-b border-[var(--chat-border)] px-4 py-3">
          <h3 className="font-semibold">Nouveau message</h3>
          <button type="button" onClick={onClose}>
            <X className="size-4" />
          </button>
        </header>
        <div className="max-h-[60vh] overflow-y-auto p-2">
          {others.length === 0 ? (
            <p className="p-3 text-sm text-[var(--chat-muted)]">Aucun collègue disponible.</p>
          ) : (
            others.map((c) => (
              <ColleagueRow
                key={c.id}
                colleague={c}
                presence={presence}
                onPick={() => onStartDm(c.id)}
                onOpenAvatarPreview={onOpenAvatarPreview}
              />
            ))
          )}
        </div>
      </div>
    </div>
  );
}

export function ChatNewVideoModal({
  selfId,
  colleagues,
  presence,
  onClose,
  onStartVideo,
  onOpenAvatarPreview,
}: Shared & {
  onClose: () => void;
  onStartVideo: (userId: number) => void;
}) {
  const others = colleagues.filter((c) => c.id !== selfId);
  return (
    <div className="fixed inset-0 z-40 flex items-end justify-center bg-black/40 p-4 sm:items-center">
      <div className="chat-panel max-h-[80vh] w-full max-w-md overflow-hidden rounded-2xl border shadow-[var(--chat-shadow)]">
        <header className="flex items-center justify-between border-b border-[var(--chat-border)] px-4 py-3">
          <div className="flex min-w-0 items-center gap-2">
            <h3 className="font-semibold">Nouvelle visio</h3>
            <span className="chat-new-badge">Nouveau</span>
          </div>
          <button type="button" onClick={onClose}>
            <X className="size-4" />
          </button>
        </header>
        <p className="border-b border-[var(--chat-border)] px-4 py-2 text-xs text-[var(--chat-muted)]">
          Choisissez un contact pour lancer un appel vidéo.
        </p>
        <div className="max-h-[60vh] overflow-y-auto p-2">
          {others.length === 0 ? (
            <p className="p-3 text-sm text-[var(--chat-muted)]">Aucun collègue disponible.</p>
          ) : (
            others.map((c) => {
              const busy = isChatPeerBusyInCall(c.id, presence, c.presence);
              return (
              <ColleagueRow
                key={c.id}
                colleague={c}
                presence={presence}
                disabled={busy}
                hint={busy ? "Déjà en visio" : undefined}
                trailing={<Video className="size-4 shrink-0 text-[var(--chat-accent-strong)]" />}
                onPick={() => onStartVideo(c.id)}
                onOpenAvatarPreview={onOpenAvatarPreview}
              />
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}

export function ChatCallPickModal({
  selected,
  selfId,
  colleagues,
  presence,
  callPickMembers,
  onToggleMember,
  onClose,
  onConfirm,
  onOpenSilent,
}: {
  selected: ChatConversation;
  selfId: number;
  colleagues: ChatColleague[];
  presence: Record<number, string>;
  callPickMembers: number[];
  onToggleMember: (id: number, checked: boolean) => void;
  onClose: () => void;
  onConfirm: () => void;
  onOpenSilent: () => void;
}) {
  return (
    <div className="fixed inset-0 z-40 flex items-end justify-center bg-black/40 p-4 sm:items-center">
      <div className="chat-panel max-h-[85vh] w-full max-w-md overflow-hidden rounded-2xl border shadow-[var(--chat-shadow)]">
        <header className="flex items-center justify-between border-b border-[var(--chat-border)] px-4 py-3">
          <h3 className="font-semibold">Qui appeler ?</h3>
          <button type="button" onClick={onClose} aria-label="Fermer">
            <X className="size-4" />
          </button>
        </header>
        <div className="space-y-3 p-4">
          <p className="text-xs leading-relaxed text-[var(--chat-muted)]">
            {selected.is_room ? "Cette salle" : "Ce groupe"} a {selected.member_ids.length} membre
            {selected.member_ids.length > 1 ? "s" : ""}. Les personnes cochées reçoivent l’appel et
            le push (max 3). Décochez pour ne pas les sonner — ils verront que la visio n’est pas
            disponible.
          </p>
          <div className="max-h-56 space-y-1 overflow-y-auto rounded-xl border border-[var(--chat-border)] p-1">
            {selected.member_ids
              .filter((id) => id !== selfId)
              .map((id) => {
                const c = colleagues.find((x) => x.id === id);
                const name =
                  c?.display_name?.trim() ||
                  c?.username ||
                  selected.members?.find((m) => m.id === id)?.display_name ||
                  `#${id}`;
                const checked = callPickMembers.includes(id);
                const busy = isChatPeerBusyInCall(id, presence, c?.presence);
                const atCap = !checked && callPickMembers.length >= 3;
                const blocked = atCap;
                return (
                  <label
                    key={id}
                    className={`flex items-center gap-3 rounded-xl px-2 py-2 ${
                      blocked
                        ? "cursor-not-allowed opacity-40"
                        : "cursor-pointer hover:bg-[var(--chat-surface-elevated)]"
                    } ${checked ? "bg-[var(--chat-accent-soft)]" : ""}`}
                  >
                    <input
                      type="checkbox"
                      className="sr-only"
                      checked={checked}
                      disabled={blocked}
                      onChange={() => {
                        if (blocked && !checked) return;
                        onToggleMember(id, checked);
                      }}
                    />
                    <ChatRoundCheck checked={checked} />
                    <AvatarBubble
                      label={name}
                      url={c?.avatar_url}
                      presence={normalizeChatPresence(presence[id])}
                      size="sm"
                    />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-medium">{name}</span>
                      {busy ? (
                        <span className="block truncate text-xs text-[var(--chat-muted)]">
                          Déjà en visio
                        </span>
                      ) : null}
                    </span>
                  </label>
                );
              })}
          </div>
          <p className="text-xs text-[var(--chat-muted)]">
            {callPickMembers.length + 1}/4 sélectionné
            {callPickMembers.length + 1 > 1 ? "s" : ""}
          </p>
          <button
            type="button"
            className="w-full rounded-xl bg-[var(--chat-accent)] px-3 py-2.5 text-sm font-semibold text-[#0f172a] disabled:opacity-40"
            disabled={callPickMembers.length === 0}
            onClick={onConfirm}
          >
            Lancer la visio
          </button>
          <button
            type="button"
            className="w-full rounded-xl px-3 py-2 text-sm font-medium text-[var(--chat-muted)] hover:bg-[var(--chat-surface-elevated)]"
            onClick={onOpenSilent}
          >
            Ouvrir sans appeler
          </button>
        </div>
      </div>
    </div>
  );
}

export function ChatNewGroupModal({
  selfId,
  colleagues,
  presence,
  myClientIds,
  groupTitle,
  groupMembers,
  groupAsVisioRoom,
  onTitleChange,
  onToggleVisioRoom,
  onToggleMember,
  onClose,
  onCreate,
  onOpenAvatarPreview,
}: Shared & {
  myClientIds: number[];
  groupTitle: string;
  groupMembers: number[];
  groupAsVisioRoom: boolean;
  onTitleChange: (value: string) => void;
  onToggleVisioRoom: (on: boolean) => void;
  onToggleMember: (id: number, checked: boolean) => void;
  onClose: () => void;
  onCreate: () => void;
}) {
  const selectedCols = colleagues.filter((c) => groupMembers.includes(c.id));
  const common = intersectColleagueClients(selectedCols, myClientIds);
  const commonCount = common?.length ?? 0;

  return (
    <div className="fixed inset-0 z-40 flex items-end justify-center bg-black/40 p-4 sm:items-center">
      <div className="chat-panel max-h-[85vh] w-full max-w-md overflow-hidden rounded-2xl border shadow-[var(--chat-shadow)]">
        <header className="flex items-center justify-between border-b border-[var(--chat-border)] px-4 py-3">
          <h3 className="font-semibold">Nouveau groupe</h3>
          <button type="button" onClick={onClose}>
            <X className="size-4" />
          </button>
        </header>
        <div className="space-y-3 p-4">
          <p className="text-xs leading-relaxed text-[var(--chat-muted)]">
            Membres et invités sont optionnels : vous pouvez créer d’abord, personnaliser, puis
            inviter depuis Infos. Cochez « salle de visio » pour limiter à 4 et activer Rejoindre.
          </p>
          <input
            value={groupTitle}
            onChange={(e) => onTitleChange(e.target.value)}
            placeholder="Nom du groupe (optionnel)"
            className="w-full rounded-xl border border-[var(--chat-border)] bg-[var(--chat-bg)] px-3 py-2 text-sm outline-none"
          />
          <label className="flex cursor-pointer items-start gap-2.5 rounded-xl border border-[var(--chat-border)] bg-[var(--chat-bg)]/50 px-3 py-2.5">
            <input
              type="checkbox"
              className="mt-0.5 size-4"
              checked={groupAsVisioRoom}
              onChange={(e) => onToggleVisioRoom(e.target.checked)}
            />
            <span className="min-w-0">
              <span className="block text-sm font-medium text-[var(--chat-text)]">Salle de visio</span>
              <span className="block text-xs text-[var(--chat-muted)]">
                Max 4 personnes (vous inclus) — même chose qu’un petit groupe, optimisé pour la visio.
              </span>
            </span>
          </label>
          <div className="max-h-56 space-y-1 overflow-y-auto">
            {colleagues
              .filter((c) => c.id !== selfId)
              .map((c) => {
                const checked = groupMembers.includes(c.id);
                const compatible = checked || colleagueSharesClients(c, common);
                const atVisioCap = groupAsVisioRoom && !checked && groupMembers.length >= 3;
                const disabled = !compatible || atVisioCap;
                const name = c.display_name?.trim() || c.username || `#${c.id}`;
                return (
                  <label
                    key={c.id}
                    className={`flex items-center gap-3 rounded-xl px-2 py-2 transition ${
                      disabled
                        ? "cursor-not-allowed opacity-40"
                        : "cursor-pointer hover:bg-[var(--chat-surface-elevated)]"
                    } ${checked ? "bg-[var(--chat-accent-soft)]" : ""}`}
                    title={
                      !compatible
                        ? "Aucun client commun avec la sélection actuelle"
                        : atVisioCap
                          ? "Maximum 3 invités en salle de visio"
                          : undefined
                    }
                  >
                    <input
                      type="checkbox"
                      className="sr-only"
                      checked={checked}
                      disabled={disabled}
                      onChange={() => {
                        if (disabled) return;
                        onToggleMember(c.id, checked);
                      }}
                    />
                    <ChatRoundCheck checked={checked} />
                    <AvatarBubble
                      label={name}
                      url={c.avatar_url}
                      presence={normalizeChatPresence(presence[c.id])}
                      size="sm"
                      onClick={
                        c.avatar_url ? () => onOpenAvatarPreview(c.avatar_url, name) : undefined
                      }
                    />
                    <span className="min-w-0 flex-1">
                      <span className="flex min-w-0 items-center gap-1.5">
                        <span className="truncate text-sm">{name}</span>
                        {(c.clients ?? []).slice(0, 2).map((cl) => (
                          <ClientPill key={cl.id} name={cl.name} />
                        ))}
                      </span>
                    </span>
                  </label>
                );
              })}
          </div>
          {groupMembers.length > 0 && commonCount === 0 ? (
            <p className="text-xs text-[var(--chat-danger)]">
              Ces personnes n’ont pas de client Chat en commun — impossible de créer le groupe.
            </p>
          ) : null}
          <button
            type="button"
            disabled={(groupAsVisioRoom && groupMembers.length > 3) || commonCount === 0}
            className="w-full rounded-xl bg-[var(--chat-accent)] py-2.5 text-sm font-semibold text-[#0f172a] disabled:opacity-40"
            onClick={onCreate}
          >
            {groupAsVisioRoom ? "Créer la salle" : "Créer le groupe"}
          </button>
        </div>
      </div>
    </div>
  );
}
